import React, { useEffect, useState, useRef } from "react";
import classNames from "classnames";
import { mapApiKey } from "../../config/google-maps";
import { Loader } from "@googlemaps/js-api-loader";
import "./style.scss";

const AutocompleteInput = React.forwardRef(({ inputProps, onAddressChanged, types = ["address"] }, ref) => {
  const [isOpen, setIsOpen] = useState(false);
  const [optionList, setOptionList] = useState([]);
  const [currentOptionIndex, setCurrentOptionIndex] = useState(-1);
  const [sessionToken, setSessionToken] = useState(null);
  const [placesService, setPlacesService] = useState(null);
  const attributionRef = useRef(null);

  useEffect(() => {
    if (!window.google) {
      const loader = new Loader({
        apiKey: mapApiKey,
        version: "weekly",
        libraries: ["places"]
      });

      loader.load().then(() => {
        if (!window.autocompleteService) {
          window.autocompleteService = new window.google.maps.places.AutocompleteService();
        }
        setPlacesService(new window.google.maps.places.PlacesService(attributionRef.current));
      });
    } else {
      setPlacesService(new window.google.maps.places.PlacesService(attributionRef.current));
    }
  }, []);

  const onComponentChange = event => {
    if (window.autocompleteService) {
      const { value } = event.target;

      if (!sessionToken) {
        setSessionToken(new window.google.maps.places.AutocompleteSessionToken());
      }

      // Google seems to throw errors if you ask it to predict input that is too short
      if (value.length > 3) {
        const predictionRequest = {
          input: value,
          types: types,
          sessionToken: sessionToken
        };

        if (navigator.geolocation) {
          navigator.geolocation.getCurrentPosition(position => {
            const coordinates = new window.google.maps.LatLng(position.coords.latitude, position.coords.longitude);
            const circle = new window.google.maps.Circle({
              center: coordinates,
              radius: position.coords.accuracy
            });
            predictionRequest.bounds = circle.getBounds();
          });
        }

        window.autocompleteService.getPlacePredictions(predictionRequest, (predictions, status) => {
          if (status === "OK") {
            setOptionList(
              predictions.map(prediction => ({
                id: prediction.place_id,
                name: prediction.description,
                primary: prediction.structured_formatting.main_text,
                secondary: prediction.structured_formatting.secondary_text
              }))
            );
            if (!isOpen) {
              setIsOpen(true);
            }
          } else {
            setOptionList([]);
          }
        });
      } else {
        setOptionList([]);
      }
    }
  };

  const onComponentFocus = event => {
    setCurrentOptionIndex(-1);
  };
  const onComponentBlur = event => {
    setIsOpen(false);
  };

  const onKeyDown = event => {
    if (!optionList.length) {
      return;
    }

    const ARROW_DOWN = 40;
    const ARROW_UP = 38;
    const ENTER = 13;

    switch (event.keyCode) {
      case ARROW_DOWN:
        if (!isOpen) {
          setIsOpen(true);
          setCurrentOptionIndex(0);
        } else {
          setCurrentOptionIndex((currentOptionIndex + 1) % optionList.length);
        }
        break;
      case ARROW_UP:
        // Modulo doesn't quite work as you'd expect with negatives
        // Also account for the fact that option index = -1 when no option is selected
        const newIndex = currentOptionIndex < 0 ? currentOptionIndex : currentOptionIndex - 1;
        setCurrentOptionIndex(newIndex < 0 ? optionList.length - 1 : newIndex);
        break;
      case ENTER:
        if (optionList.length) {
          if (optionList.length === 1) {
            selectOption(0);
          } else if (currentOptionIndex >= 0 && currentOptionIndex < optionList.length) {
            selectOption(currentOptionIndex);
          }
        }
        break;
    }
  };

  const selectOption = optionIndex => {
    setCurrentOptionIndex(optionIndex);
    setIsOpen(false);

    if (placesService) {
      placesService.getDetails(
        {
          placeId: optionList[optionIndex].id,
          fields: ["address_component"],
          sessionToken: sessionToken
        },
        (result, status) => {
          if (status === "OK") {
            const address = {};

            address.description = optionList[optionIndex].name;

            let streetNumber, streetName;

            result.address_components.forEach(component => {
              switch (component.types[0]) {
                case "street_number":
                  streetNumber = component.long_name;
                  break;
                case "route":
                  streetName = component.long_name;
                  break;
                case "neighborhood":
                case "locality":
                case "postal_town":
                  address.city = component.long_name;
                  break;
                case "administrative_area_level_1":
                  // State (for US, Brazil, Germany, etc)
                  address.state = component.long_name;
                  break;
                case "postal_code":
                  address.postalCode = component.long_name;
                  break;
                case "country":
                  address.country = component.short_name;
                  break;
              }
            });

            // This is kind of a rudimentary solution as of right now
            if (["GB", "US", "AU", "NZ", "HK"].includes(address.country)) {
              address.street = streetNumber ? streetNumber + " " + streetName : streetName;
            } else {
              address.street = streetNumber ? streetName + " " + streetNumber : streetName;
            }

            if (onAddressChanged) {
              onAddressChanged(address);
            }
          }
        }
      );
    }

    // A session token is only valid from when a user starts typing, to when details about a specific location are fetched.
    // A token represents one billing unit, so it is good practice to use one (even though it works without).
    setSessionToken(null);
  };

  const itemClasses = index => classNames("autocomplete-item", { active: index === currentOptionIndex });
  const dropdownClasses = classNames("autocomplete-dropdown", { hide: !(isOpen && optionList.length) });

  const props = { ...inputProps };
  props.onChange = event => {
    inputProps.onChange(event);
    onComponentChange(event);
  };
  props.onFocus = event => {
    inputProps.onFocus(event);
    onComponentFocus(event);
  };
  props.onBlur = event => {
    inputProps.onBlur(event);
    onComponentBlur(event);
  };
  props.onKeyDown = event => {
    onKeyDown(event);
  };

  return (
    <>
      {/* The autocomplete field must always be set to new-password, otherwise the browser autofill dropdown will conflict with the autocomplete!! */}
      <input {...props} ref={ref} autoComplete={"new-password"} />
      <ul className={dropdownClasses}>
        {optionList.map((option, index) => (
          <li className={itemClasses(index)} onMouseDown={() => selectOption(index)} onMouseOver={() => setCurrentOptionIndex(index)} key={index}>
            <span className="place-icon place-icon-marker"></span>
            <span className="primary-location">{option.primary}</span>
            <span>{option.secondary}</span>
          </li>
        ))}
        {/* This is a container for google third party attributions (authors of reviews etc.) - it will typically be empty.
            We might however also need to add a "powered by google" logo.
            https://developers.google.com/maps/documentation/places/android-sdk/attributions
            */}
        <li ref={attributionRef} className="google-attribution" />
      </ul>
    </>
  );
});

export default AutocompleteInput;
